// Path: src/components/workshoppage/WorkshopHeader.tsx
import React from 'react';
import { ArrowLeft, History, ChevronLeft, ChevronRight, Loader2, Save, Trash2, Download } from 'lucide-react';
import { type SlideDetail, type SlideVersionInfo, type SlideContextResponse } from '../../lib/api';

interface Props {
  slide: SlideDetail | null;
  versions: SlideVersionInfo[];
  slideContext: SlideContextResponse | null;
  isSaving: boolean;
  isNavigating: boolean;
  isClearing: boolean;
  isExporting: boolean;
  onBack: () => void;
  onSave: () => void;
  onClear: () => void;
  onExport: () => void;
  onVersionChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onNavigation: (dir: 'prev' | 'next') => void;
}

export const WorkshopHeader = ({
  slide, versions, slideContext, isSaving, isNavigating, isClearing, isExporting,
  onBack, onSave, onClear, onExport, onVersionChange, onNavigation
}: Props) => {
  return (
    <header className="h-14 bg-white border-b flex items-center justify-between px-4 shadow-sm z-10">
      {/* Left: Back & Title */}
      <div className="flex items-center gap-4 w-1/3">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-gray-800 text-lg whitespace-nowrap">
            {slide ? `Slide ${slide.slide_index}` : '...'}
          </h1>
          <div className="relative group">
            <History size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-600 z-10" />
            <select
              value={slide?.id}
              onChange={onVersionChange}
              className="pl-7 pr-8 py-1 bg-blue-50 border border-blue-100 text-blue-700 text-xs rounded-full appearance-none outline-none cursor-pointer hover:bg-blue-100 transition-colors font-mono font-medium"
            >
              {versions.length > 0 ? (
                versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.version_number} {v.id === slide?.id ? '(Current)' : ''}
                  </option>
                ))
              ) : <option>{slide?.version_number}</option>}
            </select>
          </div>
        </div>
      </div>

      {/* Center: Context Info & Navigation */}
      <div className="flex items-center gap-4">
        {/* Slide Context Info */}
        <div className="text-xs text-gray-600 font-mono whitespace-nowrap">
          {slideContext ? (
            <>
              Plan: {slideContext.plan.version_number} | 
              Lesson {slideContext.lesson.sort_order}: {slideContext.lesson.title} | 
              Stage {slideContext.stage.sort_order}
            </>
          ) : (
            <span className="text-gray-400">Loading context...</span>
          )}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-full border">
           <button onClick={() => onNavigation('prev')} disabled={isNavigating} className="p-1.5 rounded-full hover:bg-white hover:shadow-sm text-gray-600 disabled:opacity-30 transition-all">
             <ChevronLeft size={20} />
           </button>
           <span className="text-xs font-mono text-gray-500 px-3 select-none border-l border-r border-gray-200">
             Page {slide?.slide_index}
           </span>
           {(slide?.slide_index || 0) < 50 && (
             <button onClick={() => onNavigation('next')} disabled={isNavigating} className="p-1.5 rounded-full hover:bg-white hover:shadow-sm text-gray-600 disabled:opacity-30 transition-all">
               <ChevronRight size={20} />
             </button>
           )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3 w-1/3 justify-end">
        <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200 hidden md:flex">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Online
        </div>
        <button 
          onClick={onClear} 
          disabled={isClearing} 
          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium disabled:opacity-50"
        >
           {isClearing ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16} />} 清空
         </button>
        <button 
          onClick={onExport} 
          disabled={isExporting} 
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium disabled:opacity-50"
        >
           {isExporting ? <Loader2 size={16} className="animate-spin"/> : <Download size={16} />} 导出
         </button>
        <button onClick={onSave} disabled={isSaving} className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50">
           {isSaving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16} />} Save Changes
         </button>
      </div>
    </header>
  );
};
